import React, { useState } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sparkles, CreditCard, User, Bell, Shield, Menu, X, Settings, LogOut, MessageSquare, Edit, Save, XCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { getCreditMonitoringLink, trackAffiliateClick } from '@/utils/affiliate';
import { toast } from 'sonner';

const AccountSettings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('account');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageForm, setMessageForm] = useState({ subject: '', message: '' });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Profile edit state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [savingPassword, setSavingPassword] = useState(false);

  const handleSubscribe = async () => {
    try {
      setLoading(true);
      
      // Determine package type based on account
      let packageType = 'diy';
      if (user?.account_type === 'broker') {
        packageType = user?.broker_tier === 'enterprise' ? 'broker_enterprise' : 'broker_pro';
      }
      
      // Create Stripe checkout session
      const response = await axios.post(`${API}/payment/create-checkout`, {
        package_type: packageType,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe
      window.location.href = response.data.url;
      
    } catch (error) {
      toast.error('Failed to start checkout process');
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageForm.subject || !messageForm.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setSendingMessage(true);
    try {
      await axios.post(`${API}/messages`, messageForm);
      toast.success('Message sent to admin!');
      setShowMessageModal(false);
      setMessageForm({ subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profileForm.full_name || !profileForm.email) {
      toast.error('Please fill in all fields');
      return;
    }

    setSavingProfile(true);
    try {
      const response = await axios.patch(`${API}/auth/profile`, {
        full_name: profileForm.full_name,
        email: profileForm.email
      });
      
      toast.success('Profile updated successfully!');
      setIsEditingProfile(false);
      
      // Update user data - trigger refetch or update context
      window.location.reload(); // Simple approach - refresh to get updated user
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSavingPassword(true);
    try {
      await axios.patch(`${API}/auth/password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      
      toast.success('Password changed successfully!');
      setIsChangingPassword(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleCancelProfileEdit = () => {
    setProfileForm({
      full_name: user?.full_name || '',
      email: user?.email || ''
    });
    setIsEditingProfile(false);
  };

  const handleCancelPasswordChange = () => {
    setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    setIsChangingPassword(false);
  };

  const getSubscriptionStatus = () => {
    if (user?.subscription_status === 'active') {
      return { text: 'Active', color: 'text-green-600 bg-green-100' };
    } else if (user?.subscription_status === 'trial') {
      return { text: 'Free Trial', color: 'text-blue-600 bg-blue-100' };
    }
    return { text: 'Inactive', color: 'text-gray-600 bg-gray-100' };
  };
  
  const getPlanPrice = () => {
    if (user?.account_type === 'broker') {
      return user?.broker_tier === 'enterprise' ? '$299.00' : '$199.00';
    }
    return '$59.99';
  };
  
  const getPlanName = () => {
    if (user?.account_type === 'broker') {
      return user?.broker_tier === 'enterprise' ? 'Enterprise Plan' : 'Broker Pro Plan';
    }
    return 'DIY Plan';
  };

  const status = getSubscriptionStatus();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-heading font-bold text-gradient">DISPUTEKING.AI</span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => navigate('/dashboard')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Dashboard</button>
              <button onClick={() => navigate('/disputes')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Disputes</button>
              <button onClick={() => navigate('/education')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Learn</button>
              <button onClick={() => navigate('/settings')} className="text-violet-600 font-medium">
                <Settings className="w-5 h-5" />
              </button>
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="font-heading">
                <LogOut className="w-5 h-5 mr-2" />
                Logout
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-4">
              <button onClick={() => navigate('/dashboard')} className="block w-full text-left text-slate-600 font-medium">Dashboard</button>
              <button onClick={() => navigate('/disputes')} className="block w-full text-left text-slate-600 font-medium">Disputes</button>
              <button onClick={() => navigate('/education')} className="block w-full text-left text-slate-600 font-medium">Learn</button>
              <button onClick={() => navigate('/settings')} className="block w-full text-left text-violet-600 font-medium">Settings</button>
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="w-full justify-start">Logout</Button>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-2">Account Settings</h1>
          <p className="text-slate-600">Manage your account and subscription</p>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl p-4 border-2 border-slate-100">
              <button
                onClick={() => setActiveTab('account')}
                className={`w-full text-left px-4 py-3 rounded-2xl font-medium transition-all mb-2 ${activeTab === 'account' ? 'bg-violet-100 text-violet-600' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <User className="w-5 h-5 inline mr-2" />
                Account
              </button>
              <button
                onClick={() => setActiveTab('subscription')}
                className={`w-full text-left px-4 py-3 rounded-2xl font-medium transition-all mb-2 ${activeTab === 'subscription' ? 'bg-violet-100 text-violet-600' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <CreditCard className="w-5 h-5 inline mr-2" />
                Subscription
              </button>
              <button
                onClick={() => setActiveTab('support')}
                className={`w-full text-left px-4 py-3 rounded-2xl font-medium transition-all mb-2 ${activeTab === 'support' ? 'bg-violet-100 text-violet-600' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <MessageSquare className="w-5 h-5 inline mr-2" />
                Support
              </button>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`w-full text-left px-4 py-3 rounded-2xl font-medium transition-all mb-2 ${activeTab === 'notifications' ? 'bg-violet-100 text-violet-600' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Bell className="w-5 h-5 inline mr-2" />
                Notifications
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`w-full text-left px-4 py-3 rounded-2xl font-medium transition-all ${activeTab === 'security' ? 'bg-violet-100 text-violet-600' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <Shield className="w-5 h-5 inline mr-2" />
                Security
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            {activeTab === 'account' && (
              <div className="space-y-6">
                {/* Profile Information */}
                <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-heading font-bold">Account Information</h2>
                    {!isEditingProfile && (
                      <Button
                        onClick={() => setIsEditingProfile(true)}
                        variant="outline"
                        className="rounded-full font-heading"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Profile
                      </Button>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Full Name</Label>
                      <Input
                        value={isEditingProfile ? profileForm.full_name : user?.full_name}
                        onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                        disabled={!isEditingProfile}
                        className="rounded-xl"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={isEditingProfile ? profileForm.email : user?.email}
                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                        disabled={!isEditingProfile}
                        className="rounded-xl"
                      />
                    </div>
                    <div>
                      <Label>Member Since</Label>
                      <Input value={new Date(user?.created_at).toLocaleDateString()} disabled className="rounded-xl" />
                    </div>
                    
                    {isEditingProfile && (
                      <div className="flex gap-3 pt-4">
                        <Button
                          onClick={handleSaveProfile}
                          disabled={savingProfile}
                          className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {savingProfile ? 'Saving...' : 'Save Changes'}
                        </Button>
                        <Button
                          onClick={handleCancelProfileEdit}
                          variant="outline"
                          className="flex-1 rounded-full font-heading"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Change Password */}
                <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-heading font-bold">Password</h2>
                    {!isChangingPassword && (
                      <Button
                        onClick={() => setIsChangingPassword(true)}
                        variant="outline"
                        className="rounded-full font-heading"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Change Password
                      </Button>
                    )}
                  </div>
                  
                  {isChangingPassword ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Current Password</Label>
                        <Input
                          type="password"
                          value={passwordForm.current_password}
                          onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                          className="rounded-xl"
                          placeholder="Enter current password"
                        />
                      </div>
                      <div>
                        <Label>New Password</Label>
                        <Input
                          type="password"
                          value={passwordForm.new_password}
                          onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                          className="rounded-xl"
                          placeholder="Enter new password"
                        />
                      </div>
                      <div>
                        <Label>Confirm New Password</Label>
                        <Input
                          type="password"
                          value={passwordForm.confirm_password}
                          onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                          className="rounded-xl"
                          placeholder="Confirm new password"
                        />
                      </div>
                      
                      <div className="flex gap-3 pt-4">
                        <Button
                          onClick={handleChangePassword}
                          disabled={savingPassword}
                          className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {savingPassword ? 'Changing...' : 'Change Password'}
                        </Button>
                        <Button
                          onClick={handleCancelPasswordChange}
                          variant="outline"
                          className="flex-1 rounded-full font-heading"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-600">
                      Click "Change Password" to update your account password.
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'subscription' && (
              <div className="space-y-6">
                <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
                  <h2 className="text-2xl font-heading font-bold mb-6">Subscription Status</h2>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="text-sm text-slate-600 mb-1">Current Plan</div>
                      <div className="text-2xl font-heading font-bold">{getPlanName()} - {getPlanPrice()}/mo</div>
                    </div>
                    <span className={`px-4 py-2 rounded-full font-medium ${status.color}`}>
                      {status.text}
                    </span>
                  </div>
                  {user?.subscription_status !== 'active' && (
                    <Button
                      data-testid="activate-subscription-button"
                      onClick={handleSubscribe}
                      disabled={loading}
                      className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full py-6 font-heading font-semibold"
                    >
                      {loading ? 'Redirecting to checkout...' : 'Activate Subscription'}
                    </Button>
                  )}
                </div>

                <div className="bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-3xl p-8 text-white">
                  <h3 className="text-xl font-heading font-bold mb-4">What's Included</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                      <span>Unlimited AI dispute letters</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                      <span>3-Bureau credit monitoring</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                      <span>AI credit analysis</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                      <span>24/7 support</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                      <span>110% money-back guarantee</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
                  <h3 className="text-xl font-heading font-bold mb-4">Identity IQ Partner Benefits</h3>
                  <p className="text-slate-600 mb-4">
                    Upgrade your credit monitoring with our partner Identity IQ for enhanced features and detailed reporting.
                  </p>
                  <a
                    href={getCreditMonitoringLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackAffiliateClick(axios, user?.id)}
                  >
                    <Button variant="outline" className="rounded-full font-heading">
                      Learn More
                    </Button>
                  </a>
                </div>
              </div>
            )}

            {activeTab === 'support' && (
              <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
                <h2 className="text-2xl font-heading font-bold mb-6">Contact Support</h2>
                <p className="text-slate-600 mb-6">
                  Have a question or need help? Send us a message and we'll get back to you as soon as possible.
                </p>
                <Button
                  data-testid="contact-support-button"
                  onClick={() => setShowMessageModal(true)}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full py-6 font-heading font-semibold"
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Send Message to Admin
                </Button>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
                <h2 className="text-2xl font-heading font-bold mb-6">Notification Preferences</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-4 border-b border-slate-100">
                    <div>
                      <div className="font-medium">Dispute Updates</div>
                      <div className="text-sm text-slate-600">Get notified when bureaus respond</div>
                    </div>
                    <input type="checkbox" defaultChecked className="w-5 h-5" />
                  </div>
                  <div className="flex items-center justify-between py-4 border-b border-slate-100">
                    <div>
                      <div className="font-medium">Score Changes</div>
                      <div className="text-sm text-slate-600">Alert me of credit score changes</div>
                    </div>
                    <input type="checkbox" defaultChecked className="w-5 h-5" />
                  </div>
                  <div className="flex items-center justify-between py-4 border-b border-slate-100">
                    <div>
                      <div className="font-medium">Educational Content</div>
                      <div className="text-sm text-slate-600">Weekly tips and guides</div>
                    </div>
                    <input type="checkbox" className="w-5 h-5" />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
                <h2 className="text-2xl font-heading font-bold mb-6">Security Settings</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="font-heading font-semibold mb-4">Change Password</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Current Password</Label>
                        <Input type="password" className="rounded-xl" />
                      </div>
                      <div>
                        <Label>New Password</Label>
                        <Input type="password" className="rounded-xl" />
                      </div>
                      <Button className="bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading">
                        Update Password
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Message Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold">Send Message to Admin</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Subject</Label>
              <Input
                data-testid="message-subject-input"
                value={messageForm.subject}
                onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                placeholder="What's this about?"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                data-testid="message-content-textarea"
                value={messageForm.message}
                onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                placeholder="Tell us what's on your mind..."
                className="rounded-xl min-h-[150px]"
              />
            </div>
            <Button
              data-testid="send-message-button"
              onClick={handleSendMessage}
              disabled={sendingMessage}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full py-6 font-heading font-semibold"
            >
              {sendingMessage ? 'Sending...' : 'Send Message'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountSettings;